import { v4 as uuid } from 'uuid';
import { FormField, FormSectionChild } from '../FormAssistent.types';

// A field's `name` is its identity everywhere: React keys, the react-hook-form
// registration path (`formData.<name>`), the label-error list and the stored
// submission data. Two fields sharing a name therefore share one value and
// render unreliably — these helpers keep names (and section child ids) unique.

// Deep-copies plain data (table rows / disabled times) so the copy shares no
// array or object reference with the original.
const deepCopyData = <T>(value: T): T => (value == null ? value : (JSON.parse(JSON.stringify(value)) as T));

// Copies one section child, giving it a fresh id and cloning its own fields.
const cloneSectionChild = (child: FormSectionChild, renames: Map<string, string>): FormSectionChild => ({
    ...child,
    id: uuid(),
    linkedValues: [...(child.linkedValues ?? [])],
    condition: child.condition ? { ...child.condition } : undefined,
    fields: (child.fields ?? []).map((nested) => cloneFieldTree(nested, renames))
});

// Recursively copies a field, assigning a new name to it and to everything
// nested inside it, recording old → new in `renames`.
const cloneFieldTree = (field: FormField, renames: Map<string, string>): FormField => {
    const name = uuid();
    renames.set(field.name, name);

    const next: FormField = { ...field, name };

    if (Array.isArray(field.rules)) next.rules = [...field.rules];
    if (Array.isArray(field.options)) next.options = deepCopyData(field.options);
    if (Array.isArray(field.category_discounts)) next.category_discounts = deepCopyData(field.category_discounts);
    if (Array.isArray(field.controllingOptions)) next.controllingOptions = [...field.controllingOptions];
    if (Array.isArray(field.columns)) next.columns = [...field.columns];
    if (Array.isArray(field.rows)) next.rows = deepCopyData(field.rows);
    if (Array.isArray(field.disabledTimes)) next.disabledTimes = deepCopyData(field.disabledTimes);
    if (field.autofill && typeof field.autofill === 'object') next.autofill = { ...field.autofill };
    if (Array.isArray(field.children)) next.children = field.children.map((child) => cloneSectionChild(child, renames));

    return next;
};

// Repoints `dependsOnField` at the copied controlling field when that field was
// part of the same copy; references to fields outside the copy stay untouched.
const remapDependencies = (field: FormField, renames: Map<string, string>) => {
    if (field.dependsOnField && renames.has(field.dependsOnField)) {
        field.dependsOnField = renames.get(field.dependsOnField);
    }

    (field.children ?? []).forEach((child) =>
        (child.fields ?? []).forEach((nested) => remapDependencies(nested, renames))
    );
};

// Produces an independent copy of a field for the "Duplizieren" action: fresh
// names down the whole tree, fresh section child ids, no shared references.
export const cloneField = (field: FormField): FormField => {
    const renames = new Map<string, string>();
    const cloned = cloneFieldTree(field, renames);
    remapDependencies(cloned, renames);

    return cloned;
};

// Walks a field tree and renames every field whose name was already taken,
// keeping the first occurrence — repairs forms saved with colliding names.
const dedupeFieldNames = (fields: FormField[], seenNames: Set<string>, seenChildIds: Set<string>): FormField[] =>
    (fields ?? []).map((field) => {
        const name = !field.name || seenNames.has(field.name) ? uuid() : field.name;
        seenNames.add(name);

        const next: FormField = { ...field, name };

        if (Array.isArray(field.children)) {
            next.children = field.children.map((child) => {
                const id = !child.id || seenChildIds.has(child.id) ? uuid() : child.id;
                seenChildIds.add(id);

                return { ...child, id, fields: dedupeFieldNames(child.fields ?? [], seenNames, seenChildIds) };
            });
        }

        return next;
    });

// Entry point for the dedupe pass — run on every field set entering the builder
// (loaded form, AI-generated form) so a broken form heals when it is re-saved.
export const ensureUniqueFieldNames = (fields: FormField[]): FormField[] =>
    dedupeFieldNames(fields, new Set<string>(), new Set<string>());
